/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { MaterialSpec, RubberSpec, TableInfoSpec } from "../../../shared/contracts";
import { type IPhysicalData, ItemData, toDragPoints } from "../../core/item-data";
import { resolvePhysics } from "../../core/material";
import type { DragPoint } from "../../math/dragpoint";
import { f4 } from "../../math/float";

export class RubberData extends ItemData implements IPhysicalData {
  public height: number = f4(25);
  public hitHeight: number = f4(-1.0);
  public thickness: number = f4(8);
  public hitEvent: boolean = false;
  public szMaterial?: string;
  public elasticity: number = 0.8;
  public elasticityFalloff: number = 0;
  public friction: number = 0.3;
  public scatter: number = 0;
  public isCollidable: boolean = true;
  public isVisible: boolean = true;
  public rotX: number = 0;
  public rotY: number = 0;
  public rotZ: number = 0;
  public szPhysicsMaterial?: string;
  /** The spec values are final, so the material does not overwrite them. */
  public overwritePhysics: boolean = true;
  public dragPoints: DragPoint[] = [];

  public constructor(itemName: string) {
    super(itemName);
  }

  /**
   * Builds the data from a table spec.
   * Physics values that the spec leaves out come from the material, then from the table.
   */
  public static fromSpec(
    spec: RubberSpec,
    info: TableInfoSpec,
    materials?: ReadonlyMap<string, MaterialSpec>,
  ): RubberData {
    const data = new RubberData(spec.name);
    const phys = resolvePhysics(spec, info, materials);
    data.height = f4(spec.height);
    data.hitHeight = f4(spec.hitHeight ?? -1.0);
    data.thickness = f4(spec.thickness);
    data.hitEvent = spec.hitEvent ?? data.hitEvent;
    data.szMaterial = spec.szMaterial;
    data.szPhysicsMaterial = spec.szMaterial;
    data.elasticity = phys.elasticity;
    data.elasticityFalloff = phys.elasticityFalloff;
    data.friction = phys.friction;
    data.scatter = phys.scatter;
    data.isCollidable = spec.isCollidable ?? true;
    data.isVisible = spec.isVisible ?? true;
    data.rotX = spec.rotX ?? 0;
    data.rotY = spec.rotY ?? 0;
    data.rotZ = spec.rotZ ?? 0;
    data.dragPoints = toDragPoints(spec.dragPoints);
    return data;
  }
}
